/**
 * Entité CashRegister - Représente la caisse avec son stock de billets
 *
 * Règles métier:
 * - Suit le stock de chaque dénomination
 * - Vérifie la disponibilité avant distribution
 * - Refuse les distributions impossibles (stock insuffisant)
 * - Calcule le solde total en caisse
 */

import { Money } from '../value-objects/Money';
import { InsufficientCashError, InvalidDenominationError } from '../errors/DomainErrors';
import { DENOMINATION_VALUES } from '../../shared/constants/denominations';

export interface DenominationStock {
  readonly denomination: number;
  quantity: number;
}

export interface CashRegisterProps {
  readonly cashierId: string;
  readonly stock: Map<number, number>;
  readonly lastUpdated: Date;
}

export class CashRegister {
  private _stock: Map<number, number>;
  private readonly _cashierId: string;
  private _lastUpdated: Date;

  constructor(props: CashRegisterProps) {
    this._cashierId = props.cashierId;
    this._stock = new Map(props.stock);
    this._lastUpdated = props.lastUpdated;
  }

  /**
   * Crée une nouvelle caisse avec stock initial
   */
  static create(cashierId: string, initialStock?: Map<number, number>): CashRegister {
    const defaultStock = new Map([
      [10000, 100],
      [5000, 200],
      [2000, 300],
      [1000, 500],
      [500, 500],
      [200, 500],
      [100, 500],
      [50, 500],
      [25, 500],
      [10, 500],
      [5, 500],
    ]);

    return new CashRegister({
      cashierId,
      stock: initialStock ?? defaultStock,
      lastUpdated: new Date(),
    });
  }

  get cashierId(): string {
    return this._cashierId;
  }

  get lastUpdated(): Date {
    return this._lastUpdated;
  }

  /**
   * Calcule le solde total en caisse
   */
  getTotalBalance(): Money {
    let total = 0;
    this._stock.forEach((quantity, denomination) => {
      total += denomination * quantity;
    });
    return Money.create(total, 'XOF');
  }

  /**
   * Récupère le stock disponible par dénomination
   */
  getAvailableDenominations(): DenominationStock[] {
    const result: DenominationStock[] = [];
    this._stock.forEach((quantity, denomination) => {
      result.push({ denomination, quantity });
    });
    return result.sort((a, b) => b.denomination - a.denomination);
  }

  /**
   * Récupère la quantité disponible pour une dénomination
   */
  getQuantity(denomination: number): number {
    return this._stock.get(denomination) ?? 0;
  }

  /**
   * Vérifie si les billets demandés sont disponibles
   */
  canDispense(requested: Map<number, number>): boolean {
    for (const [denomination, requestedQty] of requested) {
      const available = this._stock.get(denomination) ?? 0;
      if (requestedQty > available) {
        return false;
      }
    }
    return true;
  }

  /**
   * Distribue des billets (retrait)
   * Lance une erreur si stock insuffisant
   */
  dispense(requested: Map<number, number>): void {
    // Vérifier que toutes les dénominations sont valides
    for (const [denomination] of requested) {
      if (!DENOMINATION_VALUES.includes(denomination)) {
        throw new InvalidDenominationError(
          `Dénomination invalide: ${denomination}`
        );
      }
    }

    // Vérifier la disponibilité
    if (!this.canDispense(requested)) {
      const missing: string[] = [];
      for (const [denomination, requestedQty] of requested) {
        const available = this._stock.get(denomination) ?? 0;
        if (requestedQty > available) {
          missing.push(
            `${denomination} FCFA: demandé ${requestedQty}, disponible ${available}`
          );
        }
      }
      throw new InsufficientCashError(
        `Billets insuffisants en caisse:\n${missing.join('\n')}`
      );
    }

    // Distribuer (décrémenter le stock)
    for (const [denomination, qty] of requested) {
      const current = this._stock.get(denomination) ?? 0;
      this._stock.set(denomination, current - qty);
    }

    this._lastUpdated = new Date();
  }

  /**
   * Reçoit des billets (versement)
   */
  receive(bills: Map<number, number>): void {
    // Vérifier que toutes les dénominations sont valides
    for (const [denomination] of bills) {
      if (!DENOMINATION_VALUES.includes(denomination)) {
        throw new InvalidDenominationError(
          `Dénomination invalide: ${denomination}`
        );
      }
    }

    // Ajouter au stock
    for (const [denomination, qty] of bills) {
      const current = this._stock.get(denomination) ?? 0;
      this._stock.set(denomination, current + qty);
    }

    this._lastUpdated = new Date();
  }

  /**
   * Vérifie que le total du billetage correspond au montant attendu
   */
  validateDenominationTotal(bills: Map<number, number>, expectedTotal: number): boolean {
    let total = 0;
    bills.forEach((qty, denom) => {
      total += denom * qty;
    });
    return total === expectedTotal;
  }

  /**
   * Calcule le montant total d'un ensemble de billets
   */
  calculateTotal(bills: Map<number, number>): number {
    let total = 0;
    bills.forEach((qty, denom) => {
      total += denom * qty;
    });
    return total;
  }

  /**
   * Vérifie si la caisse est vide
   */
  isEmpty(): boolean {
    return this.getTotalBalance().isZero();
  }

  /**
   * Vérifie si une dénomination spécifique est disponible
   */
  hasDenomination(denomination: number, quantity: number): boolean {
    const available = this._stock.get(denomination) ?? 0;
    return available >= quantity;
  }

  /**
   * Sérialise pour persistence
   */
  toJSON(): Record<string, unknown> {
    return {
      cashierId: this._cashierId,
      stock: Array.from(this._stock.entries()),
      lastUpdated: this._lastUpdated.toISOString(),
    };
  }

  /**
   * Désérialise depuis persistence
   */
  static fromJSON(json: Record<string, any>): CashRegister {
    return new CashRegister({
      cashierId: json.cashierId,
      stock: new Map(json.stock),
      lastUpdated: new Date(json.lastUpdated),
    });
  }
}
